import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Context } from '../../../context/UserContext';

import { useDispatch } from 'react-redux';
import { selectComponent, clearSelectedComponent } from '../../../store/reducers/actions';

import { useLocation } from 'react-router-dom';
import { useNavigateParams } from '../../../hooks/useNavigateParams';

import { useMRTState } from '../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { useMediaQuery, useTheme } from '@mui/material';

import { MaterialReactTable } from 'material-react-table';
import { ThemeProvider } from '@mui/material';
import tableThemeCustom from '../../form/tableThemeCustom';

import useFlashMessage from '../../../hooks/useFlashMessage';
import CustomDialog from '../../layout/CustomDialog';

import { Box, Button, IconButton, Tooltip, Typography, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { Delete, Edit, Print } from '@mui/icons-material';

import PrintIcon from '@mui/icons-material/Print';
import Groups2Icon from '@mui/icons-material/Groups2';

import tratar from '../../../utils/retornaTratamentoURL';
import * as PessoaAPI from '../../../api/pessoa';

import { obterPermissoesUsuario, printPdf } from '../../../utils/funcoes';
import { relatorios } from '../../../api/relatorios';

import { podeImprimir, podeIncluir, podeEditar, podeCancelar, podeImprimirGerencia } from '../../../hooks/usePermisionMRT';

const Index = () => {
    const [pessoas, setPessoas] = useState([]);
    const [token] = useState(localStorage.getItem('token')) || '';
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const location = useLocation();
    const newUrlParams = new URLSearchParams(location.search);
    const [urlParams, setUrlParams] = useState(new URLSearchParams(location.search));

    const pathname = '/Pessoa/CreateOrEdit';
    const ul = urlParams.get('ul') || 2;
    const li = urlParams.get('li') || 3;
    const tipo = urlParams.get('tipo');
    const operacaoUUID = urlParams.get('operacao');
    const uuid = urlParams.get('uuid');
    const btnText = urlParams.get('btnText') || 'Cadastrar';

    const consultaPessoa =
        {
            3: 'pessoa',
            63: 'funcionario',
            64: 'usuario',
        }[li] || null;

    const tituloForm =
        {
            funcionario: 'Relação de Funcionários / Colaboradores',
            usuario: 'Relação de Usuários',
            pessoa: 'Relação de Pessoas',
        }[consultaPessoa] || 'Relação de Pessoas';

    const params = new URLSearchParams({
        ul: ul,
        li: li,
        tipo: tipo,
        operacao: operacaoUUID,
        uuid: uuid,
        btnText: btnText,
        consultaPessoa: consultaPessoa,
    });

    const { parametro } = useContext(Context);

    const enumTipo = [
        { label: 'Ambos', value: 'Ambos' },
        { label: 'Cliente', value: 'Cliente' },
        { label: 'Fornecedor', value: 'Fornecedor' },
    ];

    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});

    const dispatch = useDispatch();

    if (localStorage.path.replace(/["]/g, '') !== window.location.pathname) {
        tratar.LimparFiltros();
        localStorage.setItem('path', JSON.stringify(window.location.pathname));
    }

    const mrt = useMRTState({
        storageKey: `pessoa.index.${consultaPessoa || 'pessoa'}`,
        pageSizeDefault: parametro?.paginacao ?? 10,
    });

    const fetchData = async () => {
        if (!pessoas.length) {
            mrt.setIsLoading(true);
        } else {
            mrt.setIsRefetching(true);
        }

        try {
            const response = await PessoaAPI.listar(
                mrt.pagination.pageIndex * mrt.pagination.pageSize,
                mrt.pagination.pageSize,
                JSON.stringify(mrt.columnFilters ?? []),
                JSON.stringify(mrt.columnFilterFns ?? []),
                mrt.globalFilter ?? '',
                JSON.stringify(mrt.sorting ?? []),
                consultaPessoa
            );

            setPessoas(response.data.data);
            mrt.setRowCount(response.data.meta.totalRowCount);
            mrt.setIsError(false);
        } catch (error) {
            console.error(error);
            mrt.setIsError(true);
        } finally {
            mrt.setIsLoading(false);
            mrt.setIsRefetching(false);
        }
    };

    const handleOpenDialog = (row) => {
        setOpenDialog(true);
        setSelectedRow(row);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmDialog = async () => {
        let msgType = 'success';

        if (selectedRow) {
            try {
                const response = await PessoaAPI.deletar(selectedRow);
                setFlashMessage(response.data.message, msgType);
                fetchData();
            } catch (error) {
                msgType = 'error';
                console.error(error);
                setFlashMessage(error.response?.data?.message || 'Erro ao deletar registro.', msgType);
            }
        }

        setOpenDialog(false);
    };

    useEffect(() => {
        if (!token) {
            return navigate('/Auth', { replace: true });
        }

        obterPermissoesUsuario(localStorage.getItem('pessoaId'), localStorage.getItem('empresaId'), ul, li);

        const areUrlParamsDifferent = () => {
            const currentKeys = Array.from(urlParams.keys());
            const newKeys = Array.from(newUrlParams.keys());

            if (currentKeys.length !== newKeys.length) {
                return true;
            }

            for (let i = 0; i < currentKeys.length; i++) {
                if (currentKeys[i] !== newKeys[i]) {
                    return true;
                }
            }

            for (const key of currentKeys) {
                const currentValue = urlParams.get(key);
                const newValue = newUrlParams.get(key);

                if (currentValue !== newValue) {
                    return true;
                }
            }

            return false;
        };

        if (areUrlParamsDifferent()) {
            setUrlParams(newUrlParams);
        }

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        token,
        mrt.columnFilterFns,
        mrt.columnFilters,
        mrt.globalFilter,
        mrt.pagination.pageIndex,
        mrt.pagination.pageSize,
        mrt.sorting,
        location.search,
        urlParams,
        ul,
        li,
    ]);

    const getColumnsByTipo = (consultaPessoa, tituloForm) => {
        if (consultaPessoa === 'funcionario') {
            return [
                {
                    id: 'Pessoa',
                    header: tituloForm,
                    columns: [
                        {
                            accessorKey: 'CPForCNPJ',
                            header: 'CPF ou CNPJ',
                            muiTableBodyCellProps: ({ row, cell }) => ({
                                onClick: () => {
                                    console.info(row.original);
                                },
                            }),
                        },
                        {
                            accessorKey: 'nomePrincipal',
                            header: 'Colaborador',
                            size: 250,
                            Cell: ({ row }) => (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                    }}
                                >
                                    {row.original.images !== null && (
                                        <img
                                            alt=""
                                            height={30}
                                            src={process.env.REACT_APP_API + '/imagens/' + row.original.images}
                                            loading="lazy"
                                            style={{ borderRadius: '50%' }}
                                        />
                                    )}
                                    <span>{row.original.nomePrincipal}</span>
                                </Box>
                            ),
                        },
                        {
                            accessorFn: (row) => row.rhMovimentos?.[0]?.Setor?.descricao ?? '',
                            accessorKey: 'Setor.descricao',
                            header: 'Setor',
                        },
                        {
                            accessorFn: (row) => row.rhMovimentos?.[0]?.rhCargo?.descricao ?? '',
                            accessorKey: 'rhCargo.descricao',
                            header: 'Cargo',
                        },
                        {
                            accessorFn: (row) => row.rhMovimentos?.[0]?.rhNivel?.descricao ?? '',
                            accessorKey: 'rhNivel.descricao',
                            header: 'Nível',
                        },
                        {
                            accessorFn: (row) => row.rhMovimentos?.[0]?.estagiario ?? '',
                            accessorKey: 'rhMovimentos.estagiario',
                            header: 'Estagiário',
                            type: 'boolean',
                            columnFilterModeOptions: ['equals'],
                            filterSelectOptions: [
                                { label: 'Todos', value: '' },
                                { label: 'Sim', value: '1' },
                                { label: 'Não', value: '0' },
                            ],
                            filterVariant: 'select',
                            Cell: ({ row }) => {
                                const value = row.rhMovimentos?.[0]?.estagiario;
                                return <div style={{ textAlign: 'center' }}>{value ? 'Sim' : 'Não'}</div>;
                            },
                        },
                        {
                            accessorKey: 'ativo',
                            header: 'Pessoa Ativa',
                            type: 'boolean',
                            columnFilterModeOptions: ['equals'],
                            filterSelectOptions: [
                                { label: 'Todos', value: '' },
                                { label: 'Sim', value: '1' },
                                { label: 'Não', value: '0' },
                            ],
                            filterVariant: 'select',
                            Cell: ({ row }) => {
                                const value = row.original.ativo;
                                return <div style={{ textAlign: 'center' }}>{value ? 'Sim' : 'Não'}</div>;
                            },
                        },
                        {
                            accessorKey: 'pessoaColaborador.ativo',
                            header: 'Colaborador Ativo',
                            type: 'boolean',
                            columnFilterModeOptions: ['equals'],
                            filterSelectOptions: [
                                { label: 'Todos', value: '' },
                                { label: 'Sim', value: '1' },
                                { label: 'Não', value: '0' },
                            ],
                            filterVariant: 'select',
                            Cell: ({ row }) => {
                                const value = row.original.pessoaColaborador?.ativo;
                                return <div style={{ textAlign: 'center' }}>{value ? 'Sim' : 'Não'}</div>;
                            },
                        },
                        {
                            accessorKey: 'Empresa.nomeFantasia',
                            header: 'Empresa',
                        },
                    ],
                },
            ];
        } else if (consultaPessoa === 'usuario') {
            return [
                {
                    id: 'Pessoa',
                    header: tituloForm,
                    columns: [
                        {
                            accessorKey: 'CPForCNPJ',
                            header: 'CPF ou CNPJ',
                            muiTableBodyCellProps: ({ row, cell }) => ({
                                onClick: () => {
                                    console.info(row.original);
                                },
                            }),
                        },
                        {
                            accessorKey: 'nomePrincipal',
                            header: 'Parceiro',
                            size: 250,
                            Cell: ({ row }) => (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                    }}
                                >
                                    {row.original.images !== null && (
                                        <img
                                            alt=""
                                            height={30}
                                            src={process.env.REACT_APP_API + '/imagens/' + row.original.images}
                                            loading="lazy"
                                            style={{ borderRadius: '50%' }}
                                        />
                                    )}
                                    <span>{row.original.nomePrincipal}</span>
                                </Box>
                            ),
                        },
                        {
                            accessorKey: 'ativo',
                            header: 'Ativo',
                            type: 'boolean',
                            columnFilterModeOptions: ['equals'],
                            filterSelectOptions: [
                                { label: 'Todos', value: '' },
                                { label: 'Sim', value: '1' },
                                { label: 'Não', value: '0' },
                            ],
                            filterVariant: 'select',
                            Cell: ({ row }) => {
                                const value = row.original.ativo;
                                return <div style={{ textAlign: 'center' }}>{value ? 'Sim' : 'Não'}</div>;
                            },
                        },
                        {
                            accessorKey: 'Empresa.nomeFantasia',
                            header: 'Empresa',
                        },
                    ],
                },
            ];
        }

        return [
            {
                id: 'Pessoa',
                header: tituloForm,
                columns: [
                    {
                        accessorKey: 'CPForCNPJ',
                        header: 'CPF ou CNPJ',
                        muiTableBodyCellProps: ({ row, cell }) => ({
                            onClick: () => {
                                console.info(row.original);
                            },
                        }),
                    },
                    {
                        accessorKey: 'nomePrincipal',
                        header: 'Nome',
                        size: 250,
                        Cell: ({ row }) => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                }}
                            >
                                {row.original.images !== null && (
                                    <img
                                        alt=""
                                        height={30}
                                        src={process.env.REACT_APP_API + '/imagens/' + row.original.images}
                                        loading="lazy"
                                        style={{ borderRadius: '50%' }}
                                    />
                                )}
                                <span>{row.original.nomePrincipal}</span>
                            </Box>
                        ),
                    },
                    {
                        accessorKey: 'nomeFantasia',
                        header: 'Nome Fantasia',
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        type: 'boolean',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: [
                            { label: 'Todos', value: '' },
                            { label: 'Sim', value: '1' },
                            { label: 'Não', value: '0' },
                        ],
                        filterVariant: 'select',
                        Cell: ({ row }) => {
                            const value = row.original.ativo;
                            return <div style={{ textAlign: 'center' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                ],
            },
        ];
    };

    const columns = useMemo(() => {
        return getColumnsByTipo(consultaPessoa, tituloForm);
    }, [consultaPessoa, tituloForm]);

    //funções do MRT
    const handleEditar = (row) => {
        params.set('uuid', row.id);
        params.set('btnText', 'Atualizar');

        navigate(pathname, params);
    };

    const handleImprimir = async (row) => {
        const caminho = 'fichaFuncional';
        const url = 'fichaFuncional';

        const response = await relatorios(caminho, url, {
            uuid: row.id,
            consultaPessoa,
        });

        printPdf(response.data);
    };

    const handleExcluir = (row) => {
        handleOpenDialog(row);
    };

    const theme = tableThemeCustom();

    const themeMRT = useTheme();
    const isMobile = useMediaQuery(themeMRT.breakpoints.down('sm'));

    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
        isMobile,
    });

    return (
        <div>
            <div>
                {openDialog && (
                    <CustomDialog
                        title="Delete PESSOA!"
                        content="Você tem certeza?"
                        onClose={handleCloseDialog}
                        onConfirm={handleConfirmDialog}
                    />
                )}
            </div>

            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={pessoas}
                    getRowId={(row) => row.uuid}
                    renderRowActions={
                        !isMobile
                            ? ({ row }) => {
                                  const registro = row;

                                  return (
                                      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                                          <Tooltip arrow placement="top" title="Editar">
                                              <span>
                                                  <IconButton onClick={() => handleEditar(registro)} disabled={!podeEditar()}>
                                                      <Edit />
                                                  </IconButton>
                                              </span>
                                          </Tooltip>

                                          {consultaPessoa === 'pessoa' && (
                                              <Tooltip arrow placement="bottom" title="Deletar">
                                                  <span>
                                                      <IconButton
                                                          color="error"
                                                          onClick={() => handleExcluir(registro)}
                                                          disabled={!podeCancelar()}
                                                      >
                                                          <Delete />
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                          )}

                                          {consultaPessoa === 'funcionario' && (
                                              <Tooltip arrow placement="top" title="Imprimir">
                                                  <span>
                                                      <IconButton
                                                          color="info"
                                                          onClick={() => handleImprimir(registro)}
                                                          disabled={!podeImprimirGerencia()}
                                                      >
                                                          <Print />
                                                      </IconButton>
                                                  </span>
                                              </Tooltip>
                                          )}
                                      </Box>
                                  );
                              }
                            : undefined
                    }
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => {
                                  const registro = row;

                                  return [
                                      <MenuItem
                                          key="editar"
                                          disabled={!podeEditar()}
                                          onClick={() => {
                                              handleEditar(registro);
                                              closeMenu();
                                          }}
                                      >
                                          <ListItemIcon>
                                              <Edit fontSize="small" />
                                          </ListItemIcon>

                                          <ListItemText>Editar</ListItemText>
                                      </MenuItem>,

                                      consultaPessoa === 'pessoa' && (
                                          <MenuItem
                                              key="deletar"
                                              disabled={!podeCancelar()}
                                              onClick={() => {
                                                  handleExcluir(registro);
                                                  closeMenu();
                                              }}
                                          >
                                              <ListItemIcon>
                                                  <Delete color="error" fontSize="small" />
                                              </ListItemIcon>

                                              <ListItemText>Deletar</ListItemText>
                                          </MenuItem>
                                      ),

                                      consultaPessoa === 'funcionario' && (
                                          <MenuItem
                                              key="imprimir"
                                              disabled={!podeImprimirGerencia()}
                                              onClick={() => {
                                                  handleImprimir(registro);
                                                  closeMenu();
                                              }}
                                          >
                                              <ListItemIcon>
                                                  <Print color="info" fontSize="small" />
                                              </ListItemIcon>

                                              <ListItemText>Imprimir</ListItemText>
                                          </MenuItem>
                                      ),
                                  ].filter(Boolean);
                              }
                            : undefined
                    }
                    renderTopToolbarCustomActions={({ table }) => {
                        const handleImpimirParceiro = async ({ setor }) => {
                            dispatch(clearSelectedComponent());
                            const uuids = table.getSelectedRowModel().flatRows.map((row) => row.id);
                            dispatch(selectComponent(uuids));

                            const dadosFiltrados =
                                uuids.length > 0 ? pessoas.filter((pessoa) => uuids.includes(pessoa.uuid)) : pessoas;

                            const urlSearchParams = new URLSearchParams({
                                start: `${mrt.pagination.pageIndex * mrt.pagination.pageSize}`,
                                size: `${mrt.pagination.pageSize}`,
                                filters: JSON.stringify(mrt.columnFilters ?? []),
                                filtersMode: JSON.stringify(mrt.columnFilterFns ?? []),
                                globalFilter: mrt.globalFilter ?? '',
                                sorting: JSON.stringify(mrt.sorting ?? []),
                                //tipo: urlParams.get('tipo') ?? '',
                                consultaPessoa,
                                setor: setor,
                            });

                            const response = setor
                                ? await PessoaAPI.relatorioColaboradores(urlSearchParams, {})
                                : await PessoaAPI.relatorio(urlSearchParams, dadosFiltrados);

                            printPdf(response.data);
                        };

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '8px',
                                }}
                            >
                                {consultaPessoa !== 'funcionario' && consultaPessoa !== 'usuario' && (
                                    <>
                                        <Tooltip arrow placement="top-end" title="Novo Registro">
                                            <div>
                                                <Button
                                                    color="secondary"
                                                    onClick={() => {
                                                        params.delete('uuid');
                                                        params.set('btnText', 'Cadastrar');
                                                        navigate(pathname, params);
                                                    }}
                                                    variant="contained"
                                                    disabled={!podeIncluir()}
                                                >
                                                    Novo
                                                </Button>
                                            </div>
                                        </Tooltip>

                                        <Tooltip arrow placement="top-end" title="Imprimir Parceiro">
                                            <div>
                                                <span>
                                                    <Button
                                                        color="info"
                                                        variant="contained"
                                                        disabled={!podeImprimir()}
                                                        onClick={() => handleImpimirParceiro({ setor: false })}
                                                    >
                                                        Imprimir
                                                    </Button>
                                                </span>
                                            </div>
                                        </Tooltip>
                                    </>
                                )}

                                {consultaPessoa === 'funcionario' && (
                                    <>
                                        <Tooltip arrow placement="top-end" title="Imprimir colaboradores por setor">
                                            <div>
                                                <span>
                                                    <Button
                                                        color="primary"
                                                        variant="contained"
                                                        disabled={!podeImprimir()}
                                                        onClick={() => handleImpimirParceiro({ setor: true })}
                                                    >
                                                        <Groups2Icon />
                                                    </Button>
                                                </span>
                                            </div>
                                        </Tooltip>

                                        <Tooltip arrow placement="top-end" title="Imprimir colaboradores por ordem alfabetica">
                                            <div>
                                                <span>
                                                    <Button
                                                        color="info"
                                                        variant="contained"
                                                        disabled={!podeImprimir()}
                                                        onClick={() => handleImpimirParceiro({ setor: false })}
                                                    >
                                                        <PrintIcon />
                                                    </Button>
                                                </span>
                                            </div>
                                        </Tooltip>
                                    </>
                                )}
                            </div>
                        );
                    }}
                    renderDetailPanel={({ row }) => (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-around',
                                alignItems: 'center',
                            }}
                        >
                            {row.original.images !== null && (
                                <img
                                    alt="images"
                                    height={200}
                                    src={process.env.REACT_APP_API + '/imagens/' + row.original.images}
                                    loading="lazy"
                                    style={{ borderRadius: '50%' }}
                                />
                            )}
                            <Box sx={{ textAlign: 'left' }}>
                                <Typography variant="h4">Parceiro:</Typography>
                                <Typography variant="h4">{row.original.nomePrincipal}</Typography>
                            </Box>
                        </Box>
                    )}
                />
            </ThemeProvider>
        </div>
    );
};

export default Index;
